import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  FormGroup,
  Input,
  Label,
} from "reactstrap";

import { useFetchLocationsQuery } from "../api/cmsPanelsApi";
import { getApiErrorMessage } from "../api/errorUtils";
import {
  useFetchExchangeByIdQuery,
  useFetchExchangesListQuery,
} from "../api/exchangesApi";
import SaleItemLabel from "../components/Sales/SaleItemLabel";
import { useTranslation } from "../hooks/useTranslation";
import { formatMoney, formatTime, todayString, useDebounce } from "./salesUtils";

const COL = {
  number: { width: 100, flexShrink: 0 },
  sale: { width: 92, flexShrink: 0 },
  flow: { flex: 1, minWidth: 0 },
  time: { width: 48, flexShrink: 0 },
  balance: { width: 124, flexShrink: 0, textAlign: "right" },
};

function settlementLabel(t, direction, amount) {
  if (direction === "collect_from_customer") {
    return t("commerceCommon.collectAmount").replace("{amount}", formatMoney(amount));
  }

  if (direction === "refund_to_customer") {
    return t("commerceCommon.refundAmount").replace("{amount}", formatMoney(amount));
  }

  return t("commerceCommon.evenExchange");
}

const RecentExchangesPage = () => {
  const t = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedDate, setSelectedDate] = useState(
    String(searchParams.get("date") || "").trim() || todayString()
  );
  const [locationId, setLocationId] = useState(
    String(searchParams.get("locationId") || "").trim()
  );
  const [exchangeNumberInput, setExchangeNumberInput] = useState(
    String(searchParams.get("exchangeNumber") || "").trim()
  );
  const [selectedExchangeId, setSelectedExchangeId] = useState(() => {
    const value = Number(searchParams.get("exchangeId"));
    return Number.isInteger(value) && value > 0 ? value : null;
  });

  const debouncedExchangeNumber = useDebounce(exchangeNumberInput, 350);

  const { data: locations = [], isFetching: isFetchingLocations } =
    useFetchLocationsQuery();
  const {
    data: exchangesList,
    isFetching: isFetchingExchanges,
    error: exchangesListError,
  } = useFetchExchangesListQuery({
    date: selectedDate,
    locationId: locationId || undefined,
    exchangeNumber: debouncedExchangeNumber || undefined,
    pageSize: 30,
  });
  const {
    data: selectedExchange,
    isFetching: isFetchingExchangeDetail,
    error: exchangeDetailError,
  } = useFetchExchangeByIdQuery(selectedExchangeId, {
    skip: !selectedExchangeId,
  });

  useEffect(() => {
    if (!exchangesList?.items?.length) {
      setSelectedExchangeId(null);
      return;
    }
    if (selectedExchangeId) {
      const stillVisible = exchangesList.items.some((item) => item.id === selectedExchangeId);
      if (!stillVisible) {
        setSelectedExchangeId(null);
      }
    }
  }, [exchangesList, selectedExchangeId]);

  useEffect(() => {
    const nextParams = new URLSearchParams();

    if (selectedDate) nextParams.set("date", selectedDate);
    if (locationId) nextParams.set("locationId", locationId);
    if (debouncedExchangeNumber) nextParams.set("exchangeNumber", debouncedExchangeNumber);
    if (selectedExchangeId) nextParams.set("exchangeId", String(selectedExchangeId));

    const nextQuery = nextParams.toString();
    const currentQuery = searchParams.toString();
    if (nextQuery !== currentQuery) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [
    debouncedExchangeNumber,
    locationId,
    searchParams,
    selectedDate,
    selectedExchangeId,
    setSearchParams,
  ]);

  const selectedExchangeSummary = useMemo(
    () => exchangesList?.items?.find((item) => item.id === selectedExchangeId) || null,
    [exchangesList, selectedExchangeId]
  );

  const drawerOpen = Boolean(selectedExchangeId);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape" && selectedExchangeId) setSelectedExchangeId(null);
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [selectedExchangeId]);

  const clearFilters = () => {
    setSelectedDate(todayString());
    setLocationId("");
    setExchangeNumberInput("");
  };

  const hasActiveFilters =
    Boolean(locationId) ||
    Boolean(exchangeNumberInput.trim()) ||
    selectedDate !== todayString();

  const renderDetailLines = (lines, qtyKey, amountKey, amountFallbackKey) => (
    <div className="sp-basket-list sp-basket-list--readonly sp-scrollbar-hidden">
      <div className="sp-basket-row sp-basket-row--header" aria-hidden="true">
        <span>{t("commerceCommon.itemHeader")}</span>
        <span>{t("commerceCommon.qty")}</span>
        <span>{t("commerceCommon.price")}</span>
        <span>{t("commerceCommon.total")}</span>
      </div>
      {(lines || []).map((line) => (
        <div className="sp-basket-row" key={line.id}>
          <div className="sp-basket-row__item">
            <SaleItemLabel
              productTitle={line.productTitle}
              variantTitle={line.variantTitle}
              optionSummary={line.optionSummary}
              className="sp-item-label sp-truncate"
            />
          </div>
          <span className="sp-basket-row__qty">{line[qtyKey]}</span>
          <span className="sp-basket-row__price">
            {formatMoney(line.finalUnitPriceAmount ?? line[amountFallbackKey] ?? line.unitPriceAmount)}
          </span>
          <span className="sp-basket-row__total">{formatMoney(line[amountKey])}</span>
        </div>
      ))}
    </div>
  );

  return (
    <div className="content sales-premium sales-page history-page recent-exchanges-page">
      <Card className="sales-page-card">
        <CardHeader className="sales-page-header">
          <div>
            <div className="sp-title">{t("recentExchanges.title")}</div>
            <p className="sp-subtitle">{t("recentExchanges.subtitle")}</p>
          </div>
          <div className="sales-page-header__actions">
            {hasActiveFilters ? (
              <Button color="secondary" size="sm" type="button" onClick={clearFilters}>
                {t("products.resetFilters")}
              </Button>
            ) : null}
            <span className="sp-badge">
              {t("commerceCommon.resultsCount").replace("{count}", exchangesList?.total ?? 0)}
            </span>
          </div>
        </CardHeader>

        <CardBody className="sales-page-body">
          {exchangesListError ? (
            <Alert color="danger" className="sp-feedback-alert">
              {getApiErrorMessage(exchangesListError, t("recentExchanges.loadError"))}
            </Alert>
          ) : null}

          <div className="sp-filters-compact">
            <FormGroup>
              <Label for="recent-exchanges-date">{t("commerceCommon.date")}</Label>
              <Input
                id="recent-exchanges-date"
                type="date"
                value={selectedDate}
                onChange={(event) => setSelectedDate(event.target.value)}
                disabled={isFetchingExchanges}
              />
            </FormGroup>
            <FormGroup>
              <Label for="recent-exchanges-location">{t("commerceCommon.location")}</Label>
              <Input
                id="recent-exchanges-location"
                type="select"
                value={locationId}
                onChange={(event) => setLocationId(event.target.value)}
                disabled={isFetchingLocations || isFetchingExchanges}
              >
                <option value="">{t("commerceCommon.allLocations")}</option>
                {locations.map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name ||
                      t("commerceCommon.locationFallback").replace("{id}", location.id)}
                  </option>
                ))}
              </Input>
            </FormGroup>
            <FormGroup>
              <Label for="recent-exchanges-number">{t("commerceCommon.exchangeNumber")}</Label>
              <Input
                id="recent-exchanges-number"
                type="text"
                autoComplete="off"
                placeholder={t("commerceCommon.typeToFilter")}
                value={exchangeNumberInput}
                onChange={(event) => setExchangeNumberInput(event.target.value)}
                disabled={isFetchingExchanges}
              />
            </FormGroup>
          </div>

          <div className="sp-list-header">
            <span style={COL.number}>{t("commerceCommon.exchangeNumber")}</span>
            <span style={COL.sale}>{t("commerceCommon.fromSale")}</span>
            <span style={COL.flow}>{t("commerceCommon.summary")}</span>
            <span style={COL.time}>{t("commerceCommon.time")}</span>
            <span style={COL.balance}>{t("commerceCommon.balance")}</span>
          </div>

          {isFetchingExchanges ? (
            <div className="sp-list-empty">
              <span className="sp-text-sm sp-text-muted">{t("commerceCommon.loading")}</span>
            </div>
          ) : exchangesList?.items?.length ? (
            <div className="sp-sale-list">
              {exchangesList.items.map((item) => {
                const flowLabel = `${item.return?.summary || "—"} → ${
                  item.replacementSale?.summary || "—"
                }`;
                return (
                  <button
                    key={item.id}
                    type="button"
                    className={`sp-sale-row${selectedExchangeId === item.id ? " active" : ""}`}
                    onClick={() => setSelectedExchangeId(item.id)}
                    aria-label={`${t("commerceCommon.exchangeDetail")} ${item.exchangeNumber}`}
                  >
                    <span style={COL.number} className="sp-font-semibold">
                      {item.exchangeNumber}
                    </span>
                    <span style={COL.sale} className="sp-text-muted sp-truncate">
                      {item.originalSale?.saleNumber || "—"}
                    </span>
                    <span style={COL.flow} className="sp-text-muted sp-truncate" title={flowLabel}>
                      {flowLabel}
                    </span>
                    <span style={COL.time} className="sp-text-dim sp-text-sm">
                      {formatTime(item.completedAt)}
                    </span>
                    <span style={COL.balance} className="sp-text-sm sp-font-semibold">
                      {settlementLabel(t, item.settlementDirection, item.settlementAmount)}
                    </span>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="sp-list-empty">
              <span className="sp-text-sm sp-text-muted">
                {t("recentExchanges.noExchangesFound")}
              </span>
            </div>
          )}
        </CardBody>
      </Card>

      <div
        className={`sp-drawer-backdrop${drawerOpen ? " open" : ""}`}
        onClick={() => setSelectedExchangeId(null)}
      />

      <div className={`sp-drawer${drawerOpen ? " open" : ""}`}>
        <div className="sp-drawer-header">
          <div>
            <div className="sp-title">{t("commerceCommon.exchangeDetail")}</div>
            <p className="sp-subtitle">
              {selectedExchange?.exchangeNumber ||
                selectedExchangeSummary?.exchangeNumber ||
                ""}
            </p>
          </div>
          <button
            type="button"
            className="sp-drawer-close"
            onClick={() => setSelectedExchangeId(null)}
            aria-label={t("commerceCommon.close")}
          >
            ×
          </button>
        </div>

        <div className="sp-drawer-body">
          {exchangeDetailError ? (
            <Alert color="danger" className="sp-feedback-alert">
              {getApiErrorMessage(exchangeDetailError, t("recentExchanges.detailLoadError"))}
            </Alert>
          ) : isFetchingExchangeDetail ? (
            <span className="sp-text-sm sp-text-muted">{t("commerceCommon.loading")}</span>
          ) : selectedExchange ? (
            <>
              {selectedExchange.originalSale?.saleNumber ? (
                <div className="sp-drawer-actions">
                  <Button
                    tag={Link}
                    color="secondary"
                    size="sm"
                    to={`/admin/sales/history?date=${encodeURIComponent(
                      String(selectedExchange.originalSale.completedAt || "").slice(0, 10)
                    )}&saleNumber=${encodeURIComponent(
                      selectedExchange.originalSale.saleNumber
                    )}&saleId=${encodeURIComponent(selectedExchange.originalSale.id)}`}
                  >
                    {t("recentExchanges.openOriginalSale")}
                  </Button>
                </div>
              ) : null}

              <div className="sales-page-completed-meta">
                <div className="sales-page-completed-meta__item">
                  <span className="sp-stat-label">{t("commerceCommon.fromSale")}</span>
                  <span className="sp-stat-value">
                    {selectedExchange.originalSale?.saleNumber || "—"}
                  </span>
                </div>
                <div className="sales-page-completed-meta__item">
                  <span className="sp-stat-label">{t("commerceCommon.method")}</span>
                  <span className="sp-stat-value text-capitalize">
                    {selectedExchange.settlementMethod || t("commerceCommon.notNeeded")}
                  </span>
                </div>
                <div className="sales-page-completed-meta__item sales-page-completed-meta__item--total">
                  <span className="sp-stat-label">{t("commerceCommon.settlement")}</span>
                  <span className="sp-stat-value">
                    {settlementLabel(
                      t,
                      selectedExchange.settlementDirection,
                      selectedExchange.settlementAmount
                    )}
                  </span>
                </div>
              </div>

              <section className="sp-detail-section">
                <h3 className="sp-detail-section__title">
                  {t("commerceCommon.returnedLabel").replace(
                    "{number}",
                    selectedExchange.return?.returnNumber || ""
                  )}
                </h3>
                {selectedExchange.return?.lines?.length ? (
                  renderDetailLines(
                    selectedExchange.return.lines,
                    "returnedQuantity",
                    "refundAmount",
                    "unitPriceAmount"
                  )
                ) : (
                  <p className="sp-text-sm sp-text-muted mb-0">
                    {t("commerceCommon.noReturnedLines")}
                  </p>
                )}
              </section>

              <section className="sp-detail-section">
                <h3 className="sp-detail-section__title">
                  {t("commerceCommon.replacementLabel").replace(
                    "{number}",
                    selectedExchange.replacementSale?.saleNumber || ""
                  )}
                </h3>
                {selectedExchange.replacementSale?.lines?.length ? (
                  renderDetailLines(
                    selectedExchange.replacementSale.lines,
                    "quantity",
                    "lineSubtotalAmount",
                    "finalUnitPriceAmount"
                  )
                ) : (
                  <p className="sp-text-sm sp-text-muted mb-0">
                    {t("commerceCommon.noReplacementLines")}
                  </p>
                )}
              </section>

              <div className="sp-exchange-financial-summary">
                <div className="sp-summary-row sp-summary-row--muted">
                  <span>{t("commerceCommon.returnedItems")}</span>
                  <span>{formatMoney(selectedExchange.return?.totalRefundAmount)}</span>
                </div>
                <div className="sp-summary-row sp-summary-row--muted">
                  <span>{t("commerceCommon.replacementItems")}</span>
                  <span>{formatMoney(selectedExchange.replacementSale?.totalAmount)}</span>
                </div>
                <div className="sp-summary-row sp-summary-total sp-summary-total--hero">
                  <span>{t("commerceCommon.settlement")}</span>
                  <span>
                    {settlementLabel(
                      t,
                      selectedExchange.settlementDirection,
                      selectedExchange.settlementAmount
                    )}
                  </span>
                </div>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default RecentExchangesPage;
